import { useMemo, useState } from 'react'
import {
  Button,
  Chip,
  DataTable,
  DefinitionList,
  Drawer,
  IconButton,
  Modal,
  Pager,
  StateBlock,
  useClientPagination,
  useToast,
  type Column,
} from '@w7/ui'
import { useOneAccount } from '@/api/hooks/reports/useOneAccount'
import {
  useInvalidateBetsAfterResult,
  useInvalidatePnl,
} from '@/api/hooks/reports/usePlInvalidate'
import { invalidateFailureMessage } from '@/api/map/plInvalidate'
import { useCmMe } from '@/api/hooks/useCmMe'
import { Amount, Money } from '@/app/bits'
import { canInvalidatePlSettlement } from '@/app/staffAccess'
import { EMPTY, lll, odds } from '@/lib/format'
import {
  formatLineSessionDisplayRun,
  lineSessionDisplayRun,
} from '@/lib/lineSessionDisplayRun'
import type { PlStatementRowView } from '@/api/map/plStatement'
import {
  cardImageSrc,
  isCardCodeToken,
  type ArCasinoBetView,
  type CardGameMarketView,
  type CardGameRunnerView,
  type FundistActionView,
  type OneAccountBetUpdateView,
  type OneAccountBetView,
} from '@/api/map/oneAccount'
import { PlStatementGraphModal } from './PlStatementGraphModal'
import { canSelectBetForInvalidAfterResult } from './plInvalidateEligibility'
import { canShowPlViewGraph, plArchiveGraphFilter } from './plViewGraph'
import './reports.css'

type InvalidateModalMode = 'bets' | 'pnl' | null

const EMPTY_BETS: OneAccountBetView[] = []
const EMPTY_AR_BETS: ArCasinoBetView[] = []
const EMPTY_FUNDIST_ACTIONS: FundistActionView[] = []

/** Join header crumbs; drop empties and case-insensitive dupes (SoftGamings event=game). */
export function gameDetailSubtitle(
  title: string,
  ...parts: Array<string | null | undefined>
): string {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of [title, ...parts]) {
    const s = String(part ?? '').trim()
    if (!s) continue
    const key = s.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(s)
  }
  if (out.length <= 1) return out[0] ?? ''
  return `${out[0]} — ${out.slice(1).join(' · ')}`
}

function PlayingCardImg({ code }: { code: string }) {
  return (
    <img
      className="pl-playing-card"
      src={cardImageSrc(code)}
      alt={code || 'card'}
      width={42}
      height={58}
      loading="lazy"
      decoding="async"
    />
  )
}

function RunnerHand({ runner }: { runner: CardGameRunnerView }) {
  return (
    <div className="pl-card-runner">
      <div className="pl-card-kv">
        <span>Name :</span>
        <span>{runner.name || EMPTY}</span>
        <span>Status :</span>
        <span>{runner.status || EMPTY}</span>
        {runner.cards.length ? (
          <>
            <span>Card :</span>
            <div className="pl-card-hand">
              {runner.cards.map((c, i) => (
                <PlayingCardImg key={`${c}-${i}`} code={c} />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Lab sportsbook-detail «Card» block — Card Game only.
 * Three layouts: runners-only, joker/ante index, or index + runners.
 */
export function CardGameResultSection({ market }: { market: CardGameMarketView }) {
  const runners = market.marketRunner
  const index = market.indexCard
  const hasIndex = index.length > 0
  const hasRunners = runners.length > 0

  return (
    <div className="pl-card-result" data-testid="card-game-result">
      <h3 className="pl-card-result-title">Card</h3>
      {!hasIndex && hasRunners ? (
        <div className="pl-card-runners">
          {runners.map((r, i) => (
            <RunnerHand key={`${r.name}-${i}`} runner={r} />
          ))}
        </div>
      ) : null}
      {hasIndex && !hasRunners ? (
        <DefinitionList
          columns={2}
          items={[
            {
              label: 'Joker',
              value: index[0] != null ? <PlayingCardImg code={String(index[0])} /> : EMPTY,
            },
            {
              label: 'Ante',
              value: index[1] != null ? <PlayingCardImg code={String(index[1])} /> : EMPTY,
            },
          ]}
        />
      ) : null}
      {hasIndex && hasRunners ? (
        <>
          <div className="pl-card-index">
            <span className="pl-card-index-label">
              Index :{' '}
              {index
                .filter((t) => !isCardCodeToken(t))
                .map((t, i) => (
                  <span key={`n-${i}`} className="pl-card-index-num">
                    {String(t)}
                  </span>
                ))}
            </span>
            <div className="pl-card-hand">
              {index
                .filter((t) => isCardCodeToken(t))
                .map((t, i) => (
                  <PlayingCardImg key={`c-${i}`} code={String(t)} />
                ))}
            </div>
          </div>
          <div className="pl-card-runners">
            {runners.map((r, i) => (
              <RunnerHand key={`${r.name}-${i}`} runner={r} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

/**
 * Lab `openDetailView` → `Account/getOneAccount` → Game Detail.
 *
 * SportsBook / Racing / Card Game share the sportsbook modal columns.
 * Live Casino Evolution uses Lab ar-roulette-detail when ARbetDetails is present.
 * Live Casino Fundist (Crash) uses Lab live-casino-detail when fundistDetails is present.
 */
export function PlStatementDetailDrawer({
  row,
  currency,
  onClose,
}: {
  row: PlStatementRowView
  currency?: string
  onClose: () => void
}) {
  const query = useOneAccount(row)
  const me = useCmMe()
  const toast = useToast()
  const invalidatePnl = useInvalidatePnl()
  const invalidateBets = useInvalidateBetsAfterResult()
  const detail = query.data
  const displayCurrency = detail?.currencyType ?? currency
  const isCasinoDetail = Boolean(detail?.isLiveCasino && detail.arBetDetails)
  const isFundistDetail = Boolean(detail?.isFundist && detail.fundistDetails)
  const sportsBets = detail?.bets ?? EMPTY_BETS
  const casinoBets = detail?.arBetDetails?.bets ?? EMPTY_AR_BETS
  const fundistActions = detail?.fundistDetails?.actions ?? EMPTY_FUNDIST_ACTIONS
  const sportsPaging = useClientPagination(sportsBets, { resetKey: row.id })
  const casinoPaging = useClientPagination(casinoBets, { resetKey: `${row.id}:ar` })
  const fundistPaging = useClientPagination(fundistActions, { resetKey: `${row.id}:fundist` })

  const canInvalidate = canInvalidatePlSettlement(me.data?.caller)
  // Lab: Invalidate P&L ↔ isPNL*; Invalid Bets ↔ isInvalid (not OR'd together).
  const pnlLocked = Boolean(detail?.isPNLInvalid || detail?.isRollback)
  const invalidBetLocked = Boolean(detail?.isInvalid || detail?.isRollback)
  const detailReady = Boolean(detail) && !detail?.unavailable
  const showInvalidatePnl = canInvalidate && !pnlLocked && detailReady
  const selectableBetIds = useMemo(() => {
    if (isCasinoDetail || isFundistDetail) return [] as string[]
    return sportsBets.filter(canSelectBetForInvalidAfterResult).map((b) => b.id)
  }, [sportsBets, isCasinoDetail, isFundistDetail])
  // Lab: Invalid Bets only when settlement not isInvalid and ≥1 WON/LOST bet.
  const showInvalidateBets =
    canInvalidate &&
    !invalidBetLocked &&
    detailReady &&
    !isCasinoDetail &&
    !isFundistDetail &&
    selectableBetIds.length > 0

  const [selectedBetIds, setSelectedBetIds] = useState<Set<string>>(() => new Set())
  const [modalMode, setModalMode] = useState<InvalidateModalMode>(null)
  const [reason, setReason] = useState('')
  const [graphOpen, setGraphOpen] = useState(false)
  /** Lab sportsbook-detail Details eye → bet-detail-records (`bet.betUpdate`). */
  const [betUpdateRows, setBetUpdateRows] = useState<OneAccountBetUpdateView[] | null>(
    null,
  )
  const pending = invalidatePnl.isPending || invalidateBets.isPending
  const trimmedReason = reason.trim()
  const showViewGraph = Boolean(detail && canShowPlViewGraph(detail))
  const graphFilter =
    graphOpen && detail ? plArchiveGraphFilter(detail, row) : null

  function toggleBet(id: string) {
    if (!selectableBetIds.includes(id)) return
    setSelectedBetIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function closeModal() {
    if (pending) return
    setModalMode(null)
    setReason('')
  }

  async function submitInvalidate() {
    if (!trimmedReason || !detail?.id) return
    try {
      if (modalMode === 'pnl') {
        await invalidatePnl.mutateAsync({
          accountId: detail.id,
          comment: trimmedReason,
        })
        toast.success('P&L invalidated')
      } else if (modalMode === 'bets') {
        const betIds = [...selectedBetIds]
        if (!betIds.length) return
        await invalidateBets.mutateAsync({
          accountId: detail.id,
          betIds,
          reason: trimmedReason,
        })
        toast.success('Bets invalidated')
        setSelectedBetIds(new Set())
      }
      setModalMode(null)
      setReason('')
      // Close drawer after modal unlocks; nested body scroll locks used to stick.
      window.setTimeout(() => onClose(), 0)
    } catch (err) {
      toast.error(
        invalidateFailureMessage(err instanceof Error ? err.message : undefined),
      )
    }
  }

  const detailActionBar =
    showInvalidatePnl || showInvalidateBets || showViewGraph ? (
      <div
        className="w7-row"
        style={{ gap: 'var(--w7-s3)', marginBottom: 'var(--w7-s4)', flexWrap: 'wrap' }}
        data-testid={
          showInvalidatePnl || showInvalidateBets
            ? 'pl-invalidate-actions'
            : 'pl-detail-actions'
        }
      >
        {showInvalidateBets ? (
          <Button
            variant="secondary"
            disabled={selectedBetIds.size === 0}
            onClick={() => {
              setReason('')
              setModalMode('bets')
            }}
          >
            Invalid Bets
          </Button>
        ) : null}
        {showInvalidatePnl ? (
          <Button
            variant="secondary"
            onClick={() => {
              setReason('')
              setModalMode('pnl')
            }}
          >
            Invalidate P&L
          </Button>
        ) : null}
        {showViewGraph ? (
          <Button
            variant="secondary"
            data-testid="pl-view-graph"
            onClick={() => setGraphOpen(true)}
          >
            View Graph
          </Button>
        ) : null}
      </div>
    ) : null

  const sportsColumns = useMemo<Column<OneAccountBetView>[]>(() => {
    const card = Boolean(detail?.isCardGame)
    const cols: Column<OneAccountBetView>[] = []
    if (showInvalidateBets) {
      cols.push({
        id: 'pick',
        header: '',
        width: 40,
        cell: (bet) => {
          const selectable = canSelectBetForInvalidAfterResult(bet)
          return (
            <input
              type="checkbox"
              aria-label={`Select bet ${bet.id}`}
              checked={selectedBetIds.has(bet.id)}
              disabled={!selectable}
              onChange={() => toggleBet(bet.id)}
            />
          )
        },
      })
    }
    cols.push({
      id: 'placed',
      header: 'Place Date',
      width: 168,
      cell: (bet) => <span className="w7-dim">{bet.placedAt ? lll(bet.placedAt) : EMPTY}</span>,
      sortValue: (bet) => bet.placedAt ?? '',
    })
    if (!card) {
      cols.push({
        id: 'matched',
        header: 'Matched Date',
        width: 168,
        hideBelow: 900,
        cell: (bet) => <span className="w7-dim">{bet.matchedAt ? lll(bet.matchedAt) : EMPTY}</span>,
        sortValue: (bet) => bet.matchedAt ?? '',
      })
    }
    cols.push({
      id: 'type',
      header: 'Type',
      width: 76,
      cell: (bet) => (
        <Chip size="sm" tone={bet.type === 'LAY' ? 'lay' : 'back'}>
          {bet.type === 'LAY' ? 'Lay' : 'Back'}
        </Chip>
      ),
      sortValue: (bet) => bet.type,
    })
    if (!card) {
      cols.push({
        id: 'status',
        header: 'Status',
        cell: (bet) => {
          const invalid = String(bet.status).toUpperCase() === 'INVALID_BET'
          const reason = bet.reason?.trim()
          return (
            <span data-testid={invalid ? 'pl-bet-invalid-status' : undefined}>
              {bet.status || EMPTY}
              {invalid && reason ? (
                <span className="w7-dim" style={{ display: 'block' }} data-testid="pl-bet-invalid-reason">
                  Reason: {reason}
                </span>
              ) : null}
            </span>
          )
        },
        sortValue: (bet) => bet.status,
      })
    }
    const showRun =
      Boolean(detail?.isLineSession) || sportsBets.some((b) => b.runs != null)
    cols.push({
      id: 'selection',
      header: 'Horse Name',
      cell: (bet) => bet.selectionName || EMPTY,
      sortValue: (bet) => bet.selectionName,
    })
    if (showRun) {
      const lineSession = Boolean(detail?.isLineSession)
      cols.push({
        id: 'runs',
        header: 'Run',
        numeric: true,
        cell: (bet) => {
          if (bet.runs == null) return EMPTY
          // Back ceil / Lay floor only for Betfair line session — fancy-with-runs stays raw.
          if (lineSession) {
            return formatLineSessionDisplayRun(bet.runs, bet.type) || EMPTY
          }
          return String(bet.runs)
        },
        sortValue: (bet) => {
          if (bet.runs == null) return bet.runs
          return lineSession
            ? lineSessionDisplayRun(bet.runs, bet.type)
            : bet.runs
        },
      })
    }
    cols.push(
      {
        id: 'odds',
        header: 'Odds',
        numeric: true,
        cell: (bet) => (bet.odds != null ? odds(bet.odds) : EMPTY),
        sortValue: (bet) => bet.odds,
      },
      {
        id: 'avgOdds',
        header: 'Avg. Odds',
        numeric: true,
        hideBelow: 1100,
        cell: (bet) => (bet.avgOdds != null ? odds(bet.avgOdds) : EMPTY),
        sortValue: (bet) => bet.avgOdds,
      },
      {
        id: 'stake',
        header: 'Stake',
        numeric: true,
        cell: (bet) => <Amount value={bet.stake} currency={displayCurrency} />,
        sortValue: (bet) => bet.stake,
      },
      {
        id: 'matchedStake',
        header: 'Matched Stake',
        numeric: true,
        hideBelow: 900,
        cell: (bet) => <Amount value={bet.matchedStake} currency={displayCurrency} />,
        sortValue: (bet) => bet.matchedStake,
      },
      {
        id: 'pnl',
        header: 'Profit/Loss',
        numeric: true,
        cell: (bet) =>
          bet.pnl == null ? EMPTY : <Money value={bet.pnl} signed currency={displayCurrency} />,
        sortValue: (bet) => bet.pnl,
      },
      {
        id: 'betDetail',
        header: 'Details',
        width: 72,
        cell: (bet) =>
          bet.betUpdate.length > 0 ? (
            <IconButton
              name="eye"
              size="sm"
              label="Bet Detail"
              onClick={() => setBetUpdateRows(bet.betUpdate)}
            />
          ) : (
            EMPTY
          ),
      },
    )
    return cols
  }, [
    displayCurrency,
    detail?.isCardGame,
    detail?.isLineSession,
    sportsBets,
    showInvalidateBets,
    selectedBetIds,
  ])

  const casinoColumns = useMemo<Column<ArCasinoBetView>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        cell: (bet) => bet.code || EMPTY,
        sortValue: (bet) => bet.code,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (bet) => bet.description || EMPTY,
        sortValue: (bet) => bet.description,
      },
      {
        id: 'stake',
        header: 'Stake',
        numeric: true,
        cell: (bet) => <Amount value={bet.stake} currency={displayCurrency} />,
        sortValue: (bet) => bet.stake,
      },
      {
        id: 'payout',
        header: 'Payout',
        numeric: true,
        cell: (bet) => <Amount value={bet.payout} currency={displayCurrency} />,
        sortValue: (bet) => bet.payout,
      },
    ],
    [displayCurrency],
  )

  const fundistColumns = useMemo<Column<FundistActionView>[]>(
    () => [
      {
        id: 'actionTime',
        header: 'Action Time',
        width: 168,
        cell: (a) => <span className="w7-dim">{a.actionTime || EMPTY}</span>,
        sortValue: (a) => a.actionTime,
      },
      {
        id: 'actionAmount',
        header: 'Action Amount',
        numeric: true,
        cell: (a) => <Amount value={a.actionAmount} currency={displayCurrency} />,
        sortValue: (a) => a.actionAmount,
      },
      {
        id: 'actionAmountBonus',
        header: 'Action Amount Bonus',
        numeric: true,
        hideBelow: 900,
        cell: (a) => <Amount value={a.actionAmountBonus} currency={displayCurrency} />,
        sortValue: (a) => a.actionAmountBonus,
      },
      {
        id: 'actionAmountReal',
        header: 'Action Amount Real',
        numeric: true,
        hideBelow: 900,
        cell: (a) => <Amount value={a.actionAmountReal} currency={displayCurrency} />,
        sortValue: (a) => a.actionAmountReal,
      },
      {
        id: 'actionId',
        header: 'Action Id',
        cell: (a) => a.actionId || EMPTY,
        sortValue: (a) => a.actionId,
      },
      {
        id: 'type',
        header: 'Action Type',
        cell: (a) => a.type || EMPTY,
        sortValue: (a) => a.type,
      },
    ],
    [displayCurrency],
  )

  const subtitle = gameDetailSubtitle(
    detail?.eventName || row.eventName,
    detail?.gameName ?? row.game,
    detail?.subGame ?? row.sport,
    detail?.marketName ?? row.marketName,
  )
  const ar = detail?.arBetDetails
  const fundist = detail?.fundistDetails
  const outcome = ar?.outcomes[0]
  const detailPaging = isFundistDetail
    ? fundistPaging
    : isCasinoDetail
      ? casinoPaging
      : sportsPaging

  return (
    <Drawer
      open
      onClose={onClose}
      width="half"
      title="Game Detail"
      subtitle={subtitle}
      footer={
        detailPaging.total > detailPaging.pageSize ? (
          <Pager
            page={detailPaging.page}
            pageCount={detailPaging.pageCount}
            onChange={detailPaging.setPage}
            total={detailPaging.total}
            pageSize={detailPaging.pageSize}
          />
        ) : undefined
      }
    >
      {query.isLoading ? (
        <StateBlock icon="info" title="Loading, please wait…">
          Fetching this settlement’s bets from Account/getOneAccount.
        </StateBlock>
      ) : detail?.unavailable === 'forbidden' ? (
        <StateBlock icon="lock" title="Unauthorised access">
          This settlement is not in your hierarchy.
        </StateBlock>
      ) : detail?.unavailable === 'live-casino' ? (
        <StateBlock icon="info" title="Live casino detail is not available">
          Lab opened Kings AR or Fundist detail here. Neither ARbetDetails nor
          fundistDetails was returned for this settlement.
        </StateBlock>
      ) : detail?.unavailable === 'empty' ? (
        <StateBlock icon="info" title="No bet detail found">
          Account/getOneAccount returned no document for this row.
        </StateBlock>
      ) : isFundistDetail && fundist ? (
        <>
          {detailActionBar}
          <DefinitionList
            columns={2}
            items={[
              {
                label: 'Game Date',
                value: detail?.resultTime ? lll(detail.resultTime) : EMPTY,
              },
              { label: 'Player Name', value: detail?.username ?? row.memberName },
              { label: 'Game Type', value: fundist.gameDesc || EMPTY },
              { label: 'Game ID', value: fundist.gameId || EMPTY },
              { label: 'Game Start Time', value: fundist.startTime || EMPTY },
              { label: 'Game End Time', value: fundist.endTime || EMPTY },
              { label: 'Win/Loss', value: detail?.winLoss || EMPTY },
              {
                label: 'Win/Loss Amt',
                value: <Money value={detail?.netAmount ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: 'Commission Amt',
                value: <Amount value={detail?.commissionAmt ?? 0} currency={displayCurrency} />,
              },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s5)' }}>
            <DataTable
              label={`Bets for ${detail?.username ?? row.memberName}`}
              rows={fundistPaging.rows}
              columns={fundistColumns}
              rowKey={(a) => a.id}
              empty={
                <StateBlock icon="info" title="No Fundist actions">
                  fundistDetails had no SoftGamings actions for this round.
                </StateBlock>
              }
            />
          </div>
        </>
      ) : isCasinoDetail && ar ? (
        <>
          {detailActionBar}
          <DefinitionList
            columns={2}
            items={[
              {
                label: 'Game Date',
                value: detail?.resultTime ? lll(detail.resultTime) : EMPTY,
              },
              { label: 'Player Name', value: detail?.username ?? row.memberName },
              { label: 'Game Type', value: ar.tableName || ar.gameType || EMPTY },
              { label: 'Win/Loss', value: detail?.winLoss || EMPTY },
              {
                label: 'Win/Loss Amt',
                value: <Money value={detail?.netAmount ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: 'Commission Amt',
                value: <Amount value={detail?.commissionAmt ?? 0} currency={displayCurrency} />,
              },
            ]}
          />
          {outcome ? (
            <div style={{ marginTop: 'var(--w7-s5)' }}>
              <DefinitionList
                columns={3}
                items={[
                  { label: 'Number', value: outcome.number || EMPTY },
                  { label: 'Type', value: outcome.type || EMPTY },
                  { label: 'Color', value: outcome.color || EMPTY },
                ]}
              />
            </div>
          ) : null}
          {ar.transactionId ? (
            <p className="w7-dim" style={{ marginTop: 'var(--w7-s4)' }}>
              Transaction Id: {ar.transactionId}
            </p>
          ) : null}
          <div style={{ marginTop: 'var(--w7-s5)' }}>
            <DataTable
              label={`Bets for ${detail?.username ?? row.memberName}`}
              rows={casinoPaging.rows}
              columns={casinoColumns}
              rowKey={(bet) => bet.id}
              empty={
                <StateBlock icon="info" title="No casino bets">
                  ARbetDetails had no participant bets for this round.
                </StateBlock>
              }
            />
          </div>
        </>
      ) : (
        <>
          {detail?.isRollback || detail?.isInvalid || detail?.isPNLInvalid ? (
            <p
              className="w7-dim"
              style={{ marginBottom: 'var(--w7-s4)' }}
              data-testid="pl-settlement-status"
            >
              {detail.isRollback
                ? 'Rollback Bets — reverse settlement (actions locked)'
                : detail.isInvalid
                  ? 'Settlement invalidated — Invalid Bets locked'
                  : 'Settlement invalidated — Invalidate P&L locked (Invalid Bets still allowed)'}
            </p>
          ) : null}
          {detailActionBar}
          <DefinitionList
            columns={2}
            items={[
              {
                label: 'Game Date',
                value: detail?.resultTime ? lll(detail.resultTime) : EMPTY,
              },
              { label: 'Player Name', value: detail?.username ?? row.memberName },
              { label: 'Game Type', value: detail?.subGame || row.sport },
              { label: 'Win/Loss', value: detail?.winLoss || EMPTY },
              {
                label: 'Win Amt',
                value: <Money value={detail?.winAmt ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: detail?.subGame === 'Horse Racing' ? 'Deduction Amt' : 'Commission Amt',
                value: <Amount value={detail?.commissionAmt ?? 0} currency={displayCurrency} />,
              },
              {
                label: 'Net Amt',
                value: <Money value={detail?.netAmount ?? 0} signed currency={displayCurrency} />,
              },
              {
                label: 'Round Id',
                value: detail?.roundId || EMPTY,
              },
              { label: 'Winner Name', value: detail?.winner || EMPTY },
              { label: 'Match', value: detail?.eventName || row.eventName },
            ]}
          />
          <div style={{ marginTop: 'var(--w7-s5)' }}>
            <DataTable
              label={`Bets for ${detail?.username ?? row.memberName}`}
              rows={sportsPaging.rows}
              columns={sportsColumns}
              rowKey={(bet) => bet.id}
              empty={
                <StateBlock icon="info" title="No executed bets">
                  Lab only lists bets with amountExecuted or rtAmountExecuted greater than zero.
                </StateBlock>
              }
            />
          </div>
          {detail?.isCardGame && detail.cardMarkets[0] ? (
            <CardGameResultSection market={detail.cardMarkets[0]} />
          ) : null}
        </>
      )}
      {graphOpen ? (
        <PlStatementGraphModal
          open={graphOpen}
          filter={graphFilter}
          eventName={detail?.eventName || row.eventName}
          onClose={() => setGraphOpen(false)}
        />
      ) : null}
      <Modal
        open={modalMode != null}
        onClose={closeModal}
        title={modalMode === 'pnl' ? 'Invalidate P&L' : 'Invalid Bets'}
        subtitle={
          modalMode === 'pnl'
            ? 'Voids settlement P&L for this market (Result-API void_pnl).'
            : `Invalidate ${selectedBetIds.size} selected bet(s) after result.`
        }
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={closeModal} disabled={pending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={pending}
              disabled={
                !trimmedReason ||
                (modalMode === 'bets' && selectedBetIds.size === 0)
              }
              onClick={() => void submitInvalidate()}
            >
              Confirm
            </Button>
          </>
        }
      >
        <label className="w7-col" style={{ gap: 'var(--w7-s2)' }}>
          <span>Reason</span>
          <textarea
            value={reason}
            rows={4}
            autoFocus
            placeholder="Enter reason"
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </label>
      </Modal>
      <Modal
        open={betUpdateRows != null}
        onClose={() => setBetUpdateRows(null)}
        title="Bet Detail"
        size="lg"
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={() => setBetUpdateRows(null)}>
              Close
            </Button>
          </>
        }
      >
        <DataTable
          label="Bet match fills"
          rows={betUpdateRows ?? []}
          rowKey={(_, i) => `fill-${i}`}
          empty={<StateBlock icon="info" title="No data available!" />}
          columns={[
            {
              id: 'matchTime',
              header: 'Match Time',
              width: 168,
              cell: (u) => (
                <span className="w7-dim">{u.matchedAt ? lll(u.matchedAt) : EMPTY}</span>
              ),
            },
            {
              id: 'avgPrice',
              header: 'Matched Average Price',
              numeric: true,
              cell: (u) =>
                u.averagePriceMatched != null ? odds(u.averagePriceMatched) : EMPTY,
            },
            {
              id: 'executed',
              header: 'Amount Executed',
              numeric: true,
              cell: (u) => <Amount value={u.amountExecuted} currency={displayCurrency} />,
            },
            {
              id: 'pending',
              header: 'Amount Pending',
              numeric: true,
              cell: (u) => <Amount value={u.amountPending} currency={displayCurrency} />,
            },
            {
              id: 'voided',
              header: 'Amount Voided',
              numeric: true,
              cell: (u) =>
                u.amountVoided ? (
                  <Amount value={u.amountVoided} currency={displayCurrency} />
                ) : (
                  EMPTY
                ),
            },
            {
              id: 'cancelled',
              header: 'Amount Cancelled',
              numeric: true,
              cell: (u) => <Amount value={u.amountCancelled} currency={displayCurrency} />,
            },
          ]}
        />
      </Modal>
    </Drawer>
  )
}
